<docs>
# TheNavigation
> Exists only if nav is active in store/nav.ts

@author Sebastien Levy <sebastien@kffein.com>
</docs>


<script lang="ts">
import { templateRef } from '@vueuse/core';
import { store as scrollStore } from 'components/SmoothScroll/store';
import { store as themeStore } from 'pages/Thematique/store';
import { store as i18nStore } from 'plugins/i18n/store';
import emitter, { EVENTS } from 'plugins/store/events';
import { store as globalsStore } from 'plugins/store/globals';
import { interactWithSceneAt } from 'plugins/webgl/three';
import { gsap } from 'vendors/gsap';
import { computed, defineComponent, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { store as NavStore } from './store';

const Navigation = defineComponent({
  name: 'TheNavigation',
  setup() {
    const el = templateRef<HTMLElement>('el')
    const isOpen = computed(() => NavStore.isOpen);
    const i18nLocale: Koddein.Locale = i18nStore.locale as Koddein.Locale;
    const route = useRoute();
    const currentVariant = computed(() => themeStore.current);
    const router = useRouter();


    /**
     * Close nav when keydown on Escape
     */
    const escapeBinding = (e: any): void => {
      if (e.key === 'Escape') closeNav();
    }

    const closeNav = () => {
      NavStore.openState(false);
    }

    const handleClick = (e: PointerEvent, theme: ThemeItemCTA) => {
      NavStore.openState(!isOpen.value);
      e.preventDefault();

      if (route.name === "home") {
        interactWithSceneAt(1, {
          callback: "exitFromMainHeader",
          params: theme.url
        })
      } else if (route.name === "thematique") {
        //same route - do nothing
        if (route.path === theme.url) return;

        gsap.timeline()
          .add(() => {
            emitter.emit(EVENTS.THEME_SWITCHING);
          }, 0)
          .add(() => {
            themeStore.setNextVariant(theme.id);
            router.push({ path: theme.url });
          }, .8)

      } else {
        emitter.emit(EVENTS.PAGE_TRANSITION, { url: theme.url, bgColor: themeStore.getBgColorFromURL(theme.url) });
      }
    }

    /**
     * Observe navState and play animation
     */
    watch(
      isOpen,
      (open: any) => {
        if (open) {
          console.log(currentVariant.value);
          el.value.scrollTop = 0;

          const tlOpen = gsap.timeline({ paused: true })
            .fromTo(el.value?.querySelectorAll(".link-wrapper"), { opacity: 1, xPercent: 100 }, { opacity: 1, xPercent: 0, duration: 0.75, ease: 'power4.out', overwrite: false, stagger: 0.05 }, 0)

          window.addEventListener('keydown', escapeBinding);
          scrollStore.setScrollable(false);
          tlOpen.play();
        }
        else {
          const tlClose = gsap.timeline({ paused: true })
            .to(el.value?.querySelectorAll(".link-wrapper"), { xPercent: '100', duration: 0.5, ease: 'power4.out', overwrite: true, stagger: 0.05 })

          window.removeEventListener('keydown', escapeBinding);
          scrollStore.setScrollable(true);
          tlClose.play();
        }

      },
      { deep: false },
    );

    return {
      isOpen,
      locale: i18nLocale?.name,
      onClick: handleClick,
      routeName: computed(() => route.name as string),
      closeNav,
      themes: computed(() => globalsStore?.themes),
      currentVariant
    }
  },
})
export default Navigation
</script>

<template>
  <div ref="el"
       class="TheNavigation"
       :data-is-open="isOpen"
       data-lenis-prevent
  >
    <div class="wrapper">
      <nav v-if="themes" class="nav">
        <ul class="links">
          <li v-for="(theme, i) in themes"
              :key="`nav-main-${i}`"
              class="link-wrapper"
          >
            <ui-button class="link"
                       theme="theme"
                       :label="theme.title"
                       :subtitle="theme.subtitle"
                       :href="theme.url"
                       tag="a"
                       :variant="currentVariant?.id"
                       @click="onClick($event, theme)"
            />
          </li>
        </ul>
      </nav>
    </div>
    <div class="colorfill"
         :data-route="routeName"
         :data-variant="currentVariant?.id"
         @click="closeNav"
    />
  </div>
</template>

<style lang="stylus" scoped>
.TheNavigation
  position fixed
  right $gutter
  top 0px
  width 100vw
  height 100%
  z-index $z-nav
  color $c-white
  overflow-y scroll
  overflow-x hidden
  padding-top calc('var(--header-height) + 20px')

  &.-blurry
    trans(opacity, $duration:1s)
    transition-delay 1s
    opacity 0

  &[data-is-open="false"]
    overflow hidden
    pointer-events none

  .wrapper 
    f(column, $align: flex-end, $justify: flex-start)
    min-height 100%
    
    [data-is-open="true"]&
      pointer-events none
  
  .links
    rp(margin-bottom, 10px)

  .link
    text-align center
    [data-is-open="true"]&
      pointer-events all

  .link-wrapper
    opacity 0
    margin-bottom 5px
    &:last-child
      margin-bottom 0px

  
  .colorfill
    fixed 0
    size 100% 100%
    opacity 0
    z-index -1
    addVariantProps(background-color, $dark:true)
    trans(opacity, $duration:1s)
    [data-is-open="true"]&
      opacity .8

    &[data-route="bio"]
      background-color $c-main

    &[data-route="collection"]
      background-color $c-rose

    &[data-route="home"]
      background-color $c-rose
</style>
